import React, { useEffect, useState } from "react";
import { Modal, Upload, Button, List, Progress } from "antd";
import { InboxOutlined } from "@ant-design/icons";

// Limits are overridable per call-site; the defaults keep the historical
// behaviour for existing callers (EPForm response attachments etc.).
const FileUploadModal = ({
  visible,
  onClose,
  onUpload,
  disabled,
  maxFiles = 2,
  maxFileSizeMB = 4,
  maxTotalSizeMB = 8,
}) => {
  const [fileList, setFileList] = useState([]);
  const [error, setError] = useState("");
  const [totalSize, setTotalSize] = useState(0);

  const MAX_FILES = maxFiles;
  const MAX_FILE_SIZE_MB = maxFileSizeMB;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
  const MAX_TOTAL_SIZE_MB = maxTotalSizeMB;
  const MAX_TOTAL_SIZE_BYTES = MAX_TOTAL_SIZE_MB * 1024 * 1024;

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 MB";
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const calculateTotalSize = (files) => {
    return files.reduce((total, file) => total + file.size, 0);
  };

  const handleUpload = async () => {
    const uploadedUrls = await Promise.all(
      fileList.map((file) => onUpload(file))
    );
    onClose(uploadedUrls);
  };

  const uploadProps = {
    accept: ".pdf,.txt,.xls,.xlsx",
    beforeUpload: (file) => {
      const allowedTypes = [
        "application/pdf",
        "text/plain",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];
      // Safari/Edge sometimes leave file.type empty — fall back to extension sniff
      // so legitimate .xls / .xlsx don't get rejected on those browsers.
      const extAllowed = /\.(pdf|txt|xls|xlsx)$/i.test(file.name);

      // Check file count limit
      if (fileList.length >= MAX_FILES) {
        setError(
          `Maximum ${MAX_FILES} files allowed. Please remove a file before adding a new one.`
        );
        return Upload.LIST_IGNORE;
      }

      if (!allowedTypes.includes(file.type) && !extAllowed) {
        setError(
          `${file.name} is not a valid file type. Only PDF, TXT, XLS, XLSX files are allowed.`
        );
        return Upload.LIST_IGNORE;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        setError(
          `${file.name} exceeds the maximum size of ${MAX_FILE_SIZE_MB} MB per file. Please try a smaller file.`
        );
        return Upload.LIST_IGNORE;
      }

      const newTotalSize = totalSize + file.size;
      if (newTotalSize > MAX_TOTAL_SIZE_BYTES) {
        const remainingSize = MAX_TOTAL_SIZE_BYTES - totalSize;
        setError(
          `Adding ${file.name} (${formatFileSize(
            file.size
          )}) would exceed the total size limit of ${MAX_TOTAL_SIZE_MB} MB. Only ${formatFileSize(
            remainingSize
          )} remaining.`
        );
        return Upload.LIST_IGNORE;
      }

      setError("");
      const newFileList = [...fileList, file];
      setFileList(newFileList);
      setTotalSize(calculateTotalSize(newFileList));
      return false;
    },
    fileList,
    onRemove: (file) => {
      const newFileList = fileList.filter((item) => item.uid !== file.uid);
      setFileList(newFileList);
      setTotalSize(calculateTotalSize(newFileList));
    },
  };

  useEffect(() => {
    if (!visible) {
      setFileList([]);
      setError("");
      setTotalSize(0);
    }
  }, [visible]);

  const progressPercentage = (totalSize / MAX_TOTAL_SIZE_BYTES) * 100;

  return (
    <Modal
      open={visible}
      onCancel={() => onClose([])}
      footer={
        <Button
          type="primary"
          onClick={handleUpload}
          disabled={fileList.length === 0 || disabled}
        >
          Upload
        </Button>
      }
      title="Upload Files"
    >
      <Upload.Dragger {...uploadProps} multiple>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p>Drag and drop files here or click to select files</p>
        <p>
          (Only PDF, TXT, XLS, XLSX files allowed - Max {MAX_FILES} files,{" "}
          {MAX_FILE_SIZE_MB}MB each, Total max: {MAX_TOTAL_SIZE_MB}MB)
        </p>
      </Upload.Dragger>

      {/* File Count and Size Progress Indicator */}
      {fileList.length > 0 && (
        <div style={{ marginTop: "15px", marginBottom: "10px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "8px",
            }}
          >
            <span style={{ fontWeight: 500 }}>
              Files: {fileList.length}/{MAX_FILES}
            </span>
            <span
              style={{
                color:
                  totalSize > MAX_TOTAL_SIZE_BYTES * 0.9
                    ? "#ff4d4f"
                    : "#52c41a",
                fontWeight: 500,
              }}
            >
              {formatFileSize(totalSize)} /{" "}
              {formatFileSize(MAX_TOTAL_SIZE_BYTES)}
            </span>
          </div>
          <Progress
            percent={Math.min(progressPercentage, 100)}
            size="small"
            status={
              totalSize > MAX_TOTAL_SIZE_BYTES * 0.9 ? "exception" : "success"
            }
            showInfo={false}
          />
          <div
            style={{
              fontSize: "12px",
              color: "#666",
              marginTop: "4px",
            }}
          >
            {formatFileSize(MAX_TOTAL_SIZE_BYTES - totalSize)} remaining
          </div>
        </div>
      )}

      {error && (
        <div style={{ color: "red", marginTop: 10, fontWeight: 500 }}>
          {error}
        </div>
      )}

      {fileList.length > 0 && (
        <List
          className="mt-4"
          style={{ marginTop: "10px" }}
          bordered
          dataSource={fileList}
          renderItem={(file) => (
            <List.Item>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <span>{file.name}</span>
                <span style={{ color: "#666", fontSize: "12px" }}>
                  {formatFileSize(file.size)}
                </span>
              </div>
            </List.Item>
          )}
        />
      )}
    </Modal>
  );
};

export default FileUploadModal;
